import type { AccountInfo } from '@azure/msal-browser';
import { isE2eAuthBypassEnabled } from '@/config/env';
import { TokenStorage } from '@/services/auth/tokenStorage';

/**
 * E2E auth bypass (local Playwright lane).
 *
 * localhost cannot complete an MSAL login — Entra only registers the deployed
 * origin — so the local e2e lane seeds a dev-API session instead
 * (e2e/auth.setup.ts: the BE-issued immortal QA access token, or a god-token
 * login) into localStorage as `auth_tokens`. With VITE_E2E_AUTH_BYPASS=true the
 * route guards treat that stored session as signed in; the API client already
 * sends and refreshes MPN tokens on its own, so nothing else changes.
 *
 * The flag is refused in production builds (src/config/env.ts) and is off
 * unless the Vite process itself is started with it, so a normal
 * `pnpm mypaynow:dev` and every deployed build are unaffected.
 */

/**
 * Stand-in account so the shell has a name to show; nothing reads more.
 * Keep the name and email free of common button labels ("OK", "Save", …):
 * the shell's account menu button is named after them, and a spec's
 * `getByRole('button', { name: 'OK' })` substring-matches it — the old
 * `e2e-god-token@…` email tripped exactly that.
 */
export const E2E_BYPASS_ACCOUNT: AccountInfo = {
  homeAccountId: 'e2e-session',
  environment: 'e2e.local',
  tenantId: 'e2e',
  username: 'e2e-session@keystonelabs.local',
  localAccountId: 'e2e-session-user-id',
  name: 'E2E Session',
};

/** True only when the bypass is on AND a live MPN refresh token is stored. */
export function hasE2eBypassSession(): boolean {
  return isE2eAuthBypassEnabled() && TokenStorage.getRefreshToken() !== null;
}
